/**
 * Configure → Connection tab body.
 *
 * Sections:
 *   1. Connection summary (read-only) — externalOrgName + key tail.
 *   2. Fallback owner (SCH-3.5) — picker for tenant_integrations.fallback_owner_user_id.
 *   3. Rotate / Reconnect CTAs — sequential panels.
 *
 * Why sequential panels for rotate/reconnect (close-then-open) instead of
 * nested SlidePanels: SCH-2.1 chose the simpler/lower-risk UX — the existing
 * RotateKeyPanel (SCH-1.3) and ReconnectPanel (SCH-1.6) keep their top-level
 * entry points unchanged, and we don't need to refactor either component or
 * wrestle with z-index stacking.
 */

import { useEffect, useMemo, useState } from 'react';
import { Building2, Clock, Key, RefreshCw, UserCog, X } from 'lucide-react';
import { formatDistanceToNow, parseISO } from 'date-fns';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { useToast } from '@/components/ui/use-toast';
import { useTenant } from '@/contexts/TenantContext';
import {
  useUpdateScheduledSync,
  useUpdateSubContractorHubFallbackOwner,
} from '@/hooks/integrations/useSubContractorHubConnection';
import { supabase } from '@/lib/supabaseClient';
import type { SubContractorHubIntegration } from '@/types/integrations/subcontractorhub';

interface ConfigureConnectionTabProps {
  integration: SubContractorHubIntegration;
  onRequestRotateKey: () => void;
  onRequestReconnect: () => void;
}

interface TenantUser {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
}

export function ConfigureConnectionTab({
  integration,
  onRequestRotateKey,
  onRequestReconnect,
}: ConfigureConnectionTabProps) {
  return (
    <div className="space-y-4">
      {/* Connection summary */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <Building2 className="mt-0.5 h-5 w-5 text-muted-foreground" />
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">Connected to</p>
              <p className="font-medium">
                {integration.externalOrgName ?? 'Unknown organization'}
              </p>
              {integration.apiKeyLastFour && (
                <p className="text-sm text-muted-foreground">
                  Key ending in •••• {integration.apiKeyLastFour}
                </p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* SCH-3.5: Fallback owner section */}
      <FallbackOwnerSection integration={integration} />

      {/* SCH-4.1: Scheduled sync section */}
      <ScheduledSyncSection integration={integration} />

      {/* Rotate / Reconnect CTAs */}
      <div className="space-y-2">
        <Button
          variant="outline"
          className="w-full justify-start"
          onClick={onRequestRotateKey}
        >
          <Key className="mr-2 h-4 w-4" />
          Rotate API Key
        </Button>
        <Button
          variant="outline"
          className="w-full justify-start"
          onClick={onRequestReconnect}
        >
          <RefreshCw className="mr-2 h-4 w-4" />
          Reconnect to a different SubContractorHub account
        </Button>
      </div>

      <p className="text-xs text-muted-foreground">
        Rotating the API key keeps your existing organization and mappings.
        Reconnecting to a different account clears mappings and orphans
        previously imported projects — a typed-confirm step protects you from
        accidental swaps.
      </p>
    </div>
  );
}

// ─── Fallback owner section ────────────────────────────────────────────────

function FallbackOwnerSection({
  integration,
}: {
  integration: SubContractorHubIntegration;
}) {
  const { currentTenant } = useTenant();
  const { toast } = useToast();
  const updateMutation = useUpdateSubContractorHubFallbackOwner();
  const [users, setUsers] = useState<TenantUser[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  // Pending selection — null = "Clear fallback", undefined = "no change yet".
  const [pendingUserId, setPendingUserId] = useState<string | null | undefined>(
    undefined,
  );

  // Effective current value either from local pending state or the integration.
  const effectiveUserId =
    pendingUserId !== undefined ? pendingUserId : integration.fallbackOwnerUserId;

  // Reset pending state when the integration row changes (after a save).
  useEffect(() => {
    setPendingUserId(undefined);
  }, [integration.fallbackOwnerUserId]);

  useEffect(() => {
    let cancelled = false;
    async function loadUsers() {
      if (!currentTenant?.tenant_id) return;
      setUsersLoading(true);
      try {
        const { data, error } = await supabase
          .from('users')
          .select('id, first_name, last_name, email')
          .eq('tenant_id', currentTenant.tenant_id)
          .order('first_name', { ascending: true });
        if (error) throw error;
        if (!cancelled) setUsers((data as TenantUser[]) ?? []);
      } catch (err) {
        console.error('Failed to load tenant users for fallback picker:', err);
        if (!cancelled) {
          toast({
            title: 'Failed to load users',
            description: 'Please refresh the page.',
            variant: 'destructive',
          });
        }
      } finally {
        if (!cancelled) setUsersLoading(false);
      }
    }
    void loadUsers();
    return () => {
      cancelled = true;
    };
    // Intentionally exclude `toast` from deps — it's only used in the catch
    // branch and including it would re-run the effect on every render
    // (useToast returns a fresh function each call), causing an infinite
    // fetch loop that leaves usersLoading stuck at true.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTenant?.tenant_id]);

  const options = useMemo(
    () =>
      users.map(u => ({
        value: u.id,
        label:
          [u.first_name, u.last_name].filter(Boolean).join(' ').trim() ||
          u.email ||
          u.id,
        description: u.email ?? undefined,
      })),
    [users],
  );

  const currentUserDisplay = useMemo(() => {
    if (!integration.fallbackOwnerUserId) return null;
    const u = users.find(x => x.id === integration.fallbackOwnerUserId);
    if (!u) return integration.fallbackOwnerUserId;
    return (
      [u.first_name, u.last_name].filter(Boolean).join(' ').trim() ||
      u.email ||
      u.id
    );
  }, [integration.fallbackOwnerUserId, users]);

  const hasChange = pendingUserId !== undefined;
  const isPending = updateMutation.isPending;

  async function handleSave() {
    if (!hasChange) return;
    try {
      await updateMutation.mutateAsync({
        integrationId: integration.id,
        userId: pendingUserId ?? null,
      });
      toast({ title: 'Fallback owner updated' });
    } catch (err) {
      console.error('Failed to update fallback owner:', err);
      toast({
        title: 'Failed to save',
        description: String(err),
        variant: 'destructive',
      });
    }
  }

  return (
    <Card>
      <CardContent className="pt-6 space-y-3">
        <div className="flex items-start gap-3">
          <UserCog className="mt-0.5 h-5 w-5 text-muted-foreground" />
          <div className="flex-1 space-y-1">
            <p className="text-sm font-medium">Fallback Owner</p>
            <p className="text-xs text-muted-foreground">
              When SubContractorHub returns owner emails not found in your
              tenant, projects get assigned to this user after 3 unresolved
              strikes. Without a fallback, those projects fail to import.
            </p>
          </div>
        </div>

        <div className="space-y-2">
          {currentUserDisplay && (
            <p className="text-sm">
              <span className="text-muted-foreground">Currently:</span>{' '}
              <span className="font-medium">{currentUserDisplay}</span>
            </p>
          )}
          {!currentUserDisplay && (
            <p className="text-sm text-muted-foreground italic">
              No fallback configured.
            </p>
          )}

          <div className="flex items-center gap-2">
            <div className="flex-1">
              <SearchableSelect
                options={options}
                value={effectiveUserId ?? ''}
                onValueChange={value => setPendingUserId(value || null)}
                placeholder={
                  usersLoading
                    ? 'Loading users…'
                    : effectiveUserId
                    ? 'Change user…'
                    : 'Select a fallback user'
                }
                disabled={usersLoading || isPending}
              />
            </div>
            {effectiveUserId && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setPendingUserId(null)}
                disabled={isPending}
                title="Clear fallback"
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>

          <Button
            size="sm"
            onClick={handleSave}
            disabled={!hasChange || isPending}
          >
            {isPending ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ─── SCH-4.1: Scheduled sync section ───────────────────────────────────────

/**
 * SCH-4.1: scheduled sync engine UI. Admin opts in, picks an interval, sees
 * when the next run will fire. The pg_cron tick at the backend reads these
 * fields and dispatches a run_to_completion every interval.
 */
function ScheduledSyncSection({
  integration,
}: {
  integration: SubContractorHubIntegration;
}) {
  const { toast } = useToast();
  const updateMutation = useUpdateScheduledSync();

  // Local form state mirrors the integration's persisted values; Save button
  // becomes enabled when these diverge from the source-of-truth.
  const [enabled, setEnabled] = useState(integration.scheduledSyncEnabled);
  const [intervalUnit, setIntervalUnit] = useState<'minutes' | 'hours'>(
    integration.pollingIntervalMinutes % 60 === 0 && integration.pollingIntervalMinutes >= 60
      ? 'hours'
      : 'minutes',
  );
  const [intervalValue, setIntervalValue] = useState<number>(
    intervalUnit === 'hours'
      ? integration.pollingIntervalMinutes / 60
      : integration.pollingIntervalMinutes,
  );
  // SCH-5.7: consecutive-failure auto-pause threshold (1-20). Held as a string
  // so the field can be cleared mid-edit (SCH-BUG-8 / P-E5-8) without snapping
  // to 1; it's clamped on blur and parsed for validation/save.
  const [thresholdInput, setThresholdInput] = useState<string>(
    String(integration.autoPauseThreshold),
  );

  // Re-sync local state when the parent integration row changes (e.g., after
  // a successful save, or another admin updated it).
  useEffect(() => {
    setEnabled(integration.scheduledSyncEnabled);
    const useHours =
      integration.pollingIntervalMinutes % 60 === 0 &&
      integration.pollingIntervalMinutes >= 60;
    setIntervalUnit(useHours ? 'hours' : 'minutes');
    setIntervalValue(
      useHours
        ? integration.pollingIntervalMinutes / 60
        : integration.pollingIntervalMinutes,
    );
    setThresholdInput(String(integration.autoPauseThreshold));
  }, [
    integration.scheduledSyncEnabled,
    integration.pollingIntervalMinutes,
    integration.autoPauseThreshold,
  ]);

  const pendingIntervalMinutes =
    intervalUnit === 'hours' ? intervalValue * 60 : intervalValue;
  const intervalChanged = pendingIntervalMinutes !== integration.pollingIntervalMinutes;
  const enabledChanged = enabled !== integration.scheduledSyncEnabled;
  const intervalValid = pendingIntervalMinutes >= 15 && pendingIntervalMinutes <= 1440;
  const threshold = parseInt(thresholdInput, 10);
  const thresholdValid = !Number.isNaN(threshold) && threshold >= 1 && threshold <= 20;
  const thresholdChanged =
    thresholdValid && threshold !== integration.autoPauseThreshold;
  const dirty =
    (intervalChanged || enabledChanged || thresholdChanged) &&
    intervalValid &&
    thresholdValid;

  // SCH-BUG-9b (P-E4-7): convert the displayed number when the unit changes so
  // the actual cadence is preserved (60 min ↔ 1 hour) instead of being
  // reinterpreted (60 min → "60 hours").
  function handleUnitChange(newUnit: 'minutes' | 'hours') {
    if (newUnit === intervalUnit) return;
    const currentMinutes = intervalUnit === 'hours' ? intervalValue * 60 : intervalValue;
    setIntervalValue(
      newUnit === 'hours' ? Math.max(1, Math.round(currentMinutes / 60)) : currentMinutes,
    );
    setIntervalUnit(newUnit);
  }

  function handleIntervalChange(raw: string) {
    const parsed = parseInt(raw, 10);
    if (Number.isNaN(parsed) || parsed < 1) {
      setIntervalValue(1);
      return;
    }
    setIntervalValue(Math.min(parsed, 999));
  }

  async function handleSave() {
    const payload: {
      enabled?: boolean;
      polling_interval_minutes?: number;
      auto_pause_threshold?: number;
    } = {};
    if (enabledChanged) payload.enabled = enabled;
    if (intervalChanged) payload.polling_interval_minutes = pendingIntervalMinutes;
    if (thresholdChanged) payload.auto_pause_threshold = threshold;
    try {
      await updateMutation.mutateAsync(payload);
      // SCH-BUG-9b (P-E4-7): describe the actual change set, not stale local
      // `enabled` (which produced a misleading "disabled" toast when only the
      // interval/threshold changed). Use canonical minutes to avoid unit confusion.
      const description = enabledChanged
        ? enabled
          ? `Scheduled sync on — runs every ${pendingIntervalMinutes} min.`
          : 'Scheduled sync turned off.'
        : enabled
          ? `Updated — runs every ${pendingIntervalMinutes} min.`
          : 'Settings updated.';
      toast({ title: 'Scheduled sync updated', description });
    } catch (err) {
      toast({
        title: 'Could not update scheduled sync',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    }
  }

  function relativeTime(iso: string | null): string {
    if (!iso) return '—';
    try {
      return formatDistanceToNow(parseISO(iso), { addSuffix: true });
    } catch {
      return iso;
    }
  }

  return (
    <Card>
      <CardContent className="space-y-4 pt-6">
        <div className="flex items-start gap-2">
          <Clock className="mt-0.5 h-4 w-4 text-muted-foreground" />
          <div>
            <h3 className="text-sm font-medium">Scheduled sync</h3>
            <p className="text-xs text-muted-foreground">
              When enabled, the system checks SubContractorHub for changes at the
              configured cadence. Unchanged projects are skipped automatically —
              most ticks are nearly instant. Disable to stop scheduled runs
              (in-flight runs continue until done; toggle the integration off to
              halt mid-stream).
            </p>
          </div>
        </div>

        <div className="flex items-start gap-2">
          <Checkbox
            id="sch-scheduled-sync-enabled"
            checked={enabled}
            onCheckedChange={(c) => setEnabled(c === true)}
            disabled={updateMutation.isPending}
            className="mt-0.5"
          />
          <Label htmlFor="sch-scheduled-sync-enabled" className="cursor-pointer">
            <span className="text-sm font-medium">Enable scheduled sync</span>
          </Label>
        </div>

        <div className="space-y-2">
          <Label>Cadence</Label>
          <div className="flex gap-2">
            <Input
              type="number"
              inputMode="numeric"
              min={1}
              max={999}
              value={intervalValue}
              onChange={(e) => handleIntervalChange(e.target.value)}
              disabled={!enabled || updateMutation.isPending}
              className="w-28"
            />
            <Select
              value={intervalUnit}
              onValueChange={(v) => handleUnitChange(v as 'minutes' | 'hours')}
              disabled={!enabled || updateMutation.isPending}
            >
              <SelectTrigger className="flex-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="minutes">Minutes</SelectItem>
                <SelectItem value="hours">Hours</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {!intervalValid && (
            <p className="text-xs text-destructive">
              Interval must be between 15 minutes and 24 hours.
            </p>
          )}
        </div>

        {/* SCH-5.7: auto-pause threshold. After this many consecutive failed
            scheduled syncs the integration auto-pauses and emails admins. */}
        <div className="space-y-2">
          <Label htmlFor="sch-auto-pause-threshold">
            Auto-pause after consecutive failures
          </Label>
          <Input
            id="sch-auto-pause-threshold"
            type="number"
            inputMode="numeric"
            min={1}
            max={20}
            value={thresholdInput}
            // Free typing (incl. empty) — no mid-edit snap-to-1 (P-E5-8).
            onChange={(e) => setThresholdInput(e.target.value)}
            // Clamp to 1-20 on blur; restore the saved value if left empty/invalid.
            onBlur={() => {
              const parsed = parseInt(thresholdInput, 10);
              setThresholdInput(
                Number.isNaN(parsed)
                  ? String(integration.autoPauseThreshold)
                  : String(Math.min(Math.max(parsed, 1), 20)),
              );
            }}
            // Editable regardless of the enable toggle so it can be pre-set.
            disabled={updateMutation.isPending}
            className="w-28"
          />
          <p className="text-xs text-muted-foreground">
            After this many consecutive failed scheduled syncs, the integration
            pauses itself and emails tenant admins. Resume it from the
            integration card once the issue is fixed.
          </p>
          {!thresholdValid && (
            <p className="text-xs text-destructive">
              Threshold must be between 1 and 20.
            </p>
          )}
        </div>

        {integration.scheduledSyncEnabled && (
          <div className="grid grid-cols-2 gap-3 rounded-md border bg-muted/30 p-3 text-xs">
            <div>
              <p className="font-medium text-muted-foreground">Last scheduled</p>
              <p>{relativeTime(integration.lastScheduledSyncAt)}</p>
            </div>
            <div>
              <p className="font-medium text-muted-foreground">Next scheduled</p>
              <p>{relativeTime(integration.nextScheduledSyncAt)}</p>
            </div>
          </div>
        )}

        <div className="flex justify-end">
          <Button
            size="sm"
            onClick={handleSave}
            disabled={!dirty || updateMutation.isPending}
          >
            {updateMutation.isPending ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
